import { useMemo, useState } from "react";
import {
  Box, Grid, Card, CardContent, Typography,
  IconButton, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Divider, Checkbox, Table,
  TableHead, TableRow, TableCell, TableBody, Alert, Menu, MenuItem
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentIcon from "@mui/icons-material/Payment";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import { useApiClientQuery, useApiClientMutation } from "../../hooks/useApiClient";
import api_endpoints from "../../api";
import dayjs from "dayjs";
import { numberWithCommas } from "../../utils";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";

const maskLast4 = (last = "") => `•••• •••• •••• ${last || "••••"}`;

// fallback translation wrapper
function useSafeTranslation(namespace) {
  const { t: rawT } = useCustomTranslation(namespace);
  const t = (key, opts = {}) => {
    const translated = rawT(key, opts);
    if (translated === key) {
      return opts.fallback || key.replace(/_/g, " ");
    }
    return translated;
  };
  return { t };
}

const normalizeExpiration = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 4) return digits;
  if (digits.length === 6) return `${digits.slice(0, 2)}${digits.slice(4)}`;

  return "";
};

export default function PaymentMethodsTab({ donorId, organizationId, donorEmail, actingUserId, onTransactionsChanged }) {
  const { t } = useSafeTranslation("Payment Methods");

  // list of donor payment methods (cards + banks)
  const listQuery = useApiClientQuery(
    () => api_endpoints.listDonorPaymentMethods(donorId, organizationId),
    { queryKey: ["donor_payment_methods", donorId, organizationId], enabled: !!donorId && !!organizationId }
  );

  // edit (update info)
  const updateMutation = useApiClientMutation(
    ({ id, body }) => api_endpoints.updatePaymentMethod(id, body),
    ["donor_payment_methods", donorId, organizationId]
  );

  // --- UI state
  const [editOpen, setEditOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // edit form
  const [form, setForm] = useState({
    cardholderName: "",
    cardNumber: "",
    expMonth: "",
    expYear: "",
    ccv: "",
    billingAddress: ""
  });

  const openEdit = (pm) => {
    setSelected(pm);
    const [m, y] = (pm.expiration || "").split("/");
    setForm({
      cardholderName: pm.cardholderName || "",
      cardNumber: "",
      expMonth: m || "",
      expYear: y || "",
      ccv: "",
      billingAddress: pm.billingAddress || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    await updateMutation.mutateAsync({
      id: selected._id,
      body: {
        cardholderName: form.cardholderName,
        expiration: `${form.expMonth}/${form.expYear}`,
        billingAddress: form.billingAddress,
        cardNumber: form.cardNumber || undefined,
      },
    });
    setEditOpen(false);
  };

  // --- Mapping state
  const [mappingRows, setMappingRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [checked, setChecked] = useState({});
  const [reassignCount, setReassignCount] = useState(0);
  const [loadingMapData, setLoadingMapData] = useState(false);
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  const loadMapData = async (pm) => {
    setSelected(pm);
    setMapOpen(true);
    setLoadingMapData(true);
    try {
      const { data } = await api_endpoints.findTransactionsForPaymentMethod(
        pm._id,
        buildCardLookupParams(pm)
      );
      setMappingRows(data.rows || []);
      setSummary(data.summary || null);

      const init = {};
      let reassign = 0;
      (data.rows || []).forEach((r) => {
        init[r.transaction_id] = !!r.default_checked;
        if (r.donor && r.donor._id) reassign += 1;
      });
      setChecked(init);
      setReassignCount(reassign);
    } finally {
      setLoadingMapData(false);
    }
  };

  const mapMutation = useApiClientMutation(
    (payload) => api_endpoints.mapTransactionsToDonor(selected._id, payload),
    ["donor_transactions", "donor_payment_methods"],
    () => {
      setMessageSuccessAlert(t("payment_methods_mapping_success", { fallback: "Transactions mapped successfully" }));
      setShowSuccessAlert(true);
    }
  );

  const handleConfirmMap = async () => {
    const ids = Object.entries(checked).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { setMapOpen(false); return; }

    await mapMutation.mutateAsync({
      donor_id: donorId,
      organization_id: organizationId,
      transaction_ids: ids,
      acting_user_id: actingUserId,
      force: true,
      ...(isCard(selected)
        ? {
            firstNumbers: selected.firstNumbers || "",
            lastNumbers: selected.lastNumbers || "",
            expiration: normalizeExpiration(selected.expiration),
            cardholderName: selected.cardholderName || "",
            billingAddress: selected.billingAddress || "",
            cardType: selected.card_type || "card",
          }
        : {}),
    });
    onTransactionsChanged?.(); // to refresh transaction tab

    setMapOpen(false);
  };

  const toggleCheck = (txnId) => setChecked((p) => ({ ...p, [txnId]: !p[txnId] }));
  
  const isCard = (pm) => pm?.pm_type === "card" || pm?.instrument_type === "card";

  const buildCardLookupParams = (pm) => {
    if (!isCard(pm)) return { organization_id: organizationId };

    return {
      organization_id: organizationId,
      firstNumbers: pm.firstNumbers || "",
      lastNumbers: pm.lastNumbers || "",
      expiration: normalizeExpiration(pm.expiration),
      cardholderName: pm.cardholderName || "",
      billingAddress: pm.billingAddress || "",
      cardType: pm.card_type || "card",
    };
  };

  const getCardLabel = (pm) => {
    if (pm?.card_type === "debit") return t("debit_card", { fallback: "Debit Card" });
    if (pm?.card_type === "credit") return t("credit_card", { fallback: "Credit Card" });
    return t("card", { fallback: "Card" });
  };
  
  const methods = useMemo(() => {
    const data = Array.isArray(listQuery.data) ? listQuery.data : [];
    return data.sort((a, b) => {
      const aIsCard = isCard(a);
      const bIsCard = isCard(b);
      if (aIsCard && !bIsCard) return -1; // cards first
      if (!aIsCard && bIsCard) return 1;  // banks after cards
      return 0; // same type, maintain order
    });
  }, [listQuery.data]);

  const titleFor = (pm) => {
    if (isCard(pm)) {
      const cardLabel = getCardLabel(pm);
      return t("payment_methods_card_title", {
        type: cardLabel,
        last: pm.lastNumbers || "••••",
        fallback: `${cardLabel} ending in •••• ${pm.lastNumbers || "••••"}`
      });
    }
    // bank
    return t("payment_methods_bank_title", {
      bank: pm.bank_name || "Bank Account",
      last: pm.last4 || "••••",
      type: pm.account_type || "",
      fallback: `${pm.bank_name || "Bank Account"} •••• ${pm.last4 || "••••"}${pm.account_type ? ` (${pm.account_type})` : ""}`
    });
  };

  const shortLabelFor = (pm) => {
    if (isCard(pm)) return `•••• ${pm.lastNumbers || "••••"}`;
    return `${pm.bank_name || "Bank"} •••• ${pm.last4 || "••••"}`;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box className="flex items-center justify-between mb-2">
        <Typography variant="h6">{t("payment_methods_tab_label", { fallback: "Payment Methods" })}</Typography>
        <Chip size="small" label={t("payment_methods_admin_badge", { fallback: "Admin" })} variant="outlined" />
      </Box>
      <Divider sx={{ mb: 2 }} />

      <LoadingContainer loading={listQuery.isLoading} error={listQuery.isError}>
        {methods.length ? (
          <Grid container spacing={2}>
            {methods.map((pm) => (
              <Grid item xs={12} md={6} key={pm._id}>
                <Card variant="outlined" sx={{ position: "relative", p: 2, display: "flex", alignItems: "center" }}>
                  {/* Left side: card vs bank image */}
                  <Box sx={{ flexShrink: 0, mr: 2 }}>
                    <img
                      src={isCard(pm) ? "/card-generic.svg" : "/bank-generic.svg"}
                      alt={isCard(pm) ? getCardLabel(pm) : "Bank Account"}
                      style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 6 }}
                    />
                  </Box>

                  {/* Middle: details */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {titleFor(pm)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isCard(pm) ? (pm.cardholderName || "—") : (pm.bank_name || "—")}
                    </Typography>
                  </Box>

                  {/* Right side: settings button opens menu */}
                  {isCard(pm) && pm.can_map_transactions !== false && (
                    <IconButton
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setSelected(pm);
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "white",
                        border: "1px solid #ddd",
                        "&:hover": { bgcolor: "#f5f5f5" }
                      }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ p: 4, border: "1px dashed", borderColor: "divider", borderRadius: 2, textAlign: "center" }}>
            <PaymentIcon sx={{ mb: 1 }} />
            <Typography>{t("payment_methods_no_methods", { fallback: "No payment methods (card or bank)" })}</Typography>
          </Box>
        )}
      </LoadingContainer>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {/* <MenuItem
          onClick={() => {
            setAnchorEl(null);
            openEdit(selected);
          }}
        >
          {t("payment_methods_edit_info", { fallback: "Edit Info" })}
        </MenuItem> */}
        {isCard(selected) && selected?.can_map_transactions !== false && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              loadMapData(selected);
            }}
          >
            {t("payment_methods_transactions_mapping", { fallback: "Map Transactions" })}
          </MenuItem>
        )}
      </Menu>

      {/* EDIT MODAL : Commented until further notice */}
      {/* <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("payment_methods_edit_title", { fallback: "Edit Payment Method" })}</DialogTitle>
        <DialogContent dividers>
          <Box className="flex flex-col gap-2 mt-1">
            <TextField
              label={t("payment_methods_field_name", { fallback: "Cardholder Name" })}
              value={form.cardholderName}
              onChange={(e) => setForm((f) => ({ ...f, cardholderName: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t("payment_methods_field_card_number", { fallback: "Card Number" })}
              value={form.cardNumber}
              onChange={(e) => setForm((f) => ({ ...f, cardNumber: e.target.value.replace(/[^\d]/g, "") }))}
              fullWidth
            />
            <Box className="flex gap-2">
              <TextField
                label={t("payment_methods_field_exp_month", { fallback: "Exp Month" })}
                value={form.expMonth}
                onChange={(e) => setForm((f) => ({ ...f, expMonth: e.target.value.replace(/[^\d]/g, "").slice(0, 2) }))}
              />
              <TextField
                label={t("payment_methods_field_exp_year", { fallback: "Exp Year" })}
                value={form.expYear}
                onChange={(e) => setForm((f) => ({ ...f, expYear: e.target.value.replace(/[^\d]/g, "").slice(0, 4) }))}
              />
              <TextField
                label={t("payment_methods_field_cvv", { fallback: "CVV" })}
                value={form.ccv}
                onChange={(e) => setForm((f) => ({ ...f, ccv: e.target.value.replace(/[^\d]/g, "").slice(0, 4) }))}
              />
            </Box>
            <TextField
              label={t("payment_methods_field_billing_address", { fallback: "Billing Address" })}
              value={form.billingAddress}
              onChange={(e) => setForm((f) => ({ ...f, billingAddress: e.target.value }))}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              {t("payment_methods_edit_hint", { fallback: "Update payment method details" })}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setEditOpen(false)}>
            {t("payment_methods_cancel", { fallback: "Cancel" })}
          </Button>
          <Button variant="contained" onClick={saveEdit} disabled={updateMutation.isPending}>
            {updateMutation.isPending
              ? t("payment_methods_mapping_starting", { fallback: "Saving..." })
              : t("payment_methods_modify", { fallback: "Save" })}
          </Button>
        </DialogActions>
      </Dialog> */}

      {/* MAPPING MODAL */}
      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{t("payment_methods_mapping_title", { fallback: "Map Transactions" })}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1 }}>
            {t("payment_methods_mapping_intro", {
              last: selected ? shortLabelFor(selected) : "••••",
              email: donorEmail || t("payment_methods_this_donor", { fallback: "this customer" }),
              fallback: `Mapping transactions for ${selected ? shortLabelFor(selected) : "••••"}`
            })}
          </Typography>

          {reassignCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("payment_methods_warning_reassign", { count: reassignCount, fallback: `Warning: ${reassignCount} will be reassigned` })}
            </Alert>
          )}

          {summary && (
            <Box sx={{ display: "flex", gap: 4, mb: 2, flexWrap: "wrap" }}>
              <Chip label={t("payment_methods_summary_transactions_found", { count: summary.total_count, fallback: `Transactions: ${summary.total_count}` })} />
              <Chip label={t("payment_methods_summary_total_amount", { amount: numberWithCommas(summary.total_amount), fallback: `Total: ${numberWithCommas(summary.total_amount)}` })} />
              <Chip label={t("payment_methods_summary_unmapped", { count: summary.unmapped_count, amount: numberWithCommas(summary.unmapped_amount), fallback: `Unmapped: ${summary.unmapped_count}` })} />
              <Chip label={t("payment_methods_summary_mapped", { count: summary.mapped_count, amount: numberWithCommas(summary.mapped_amount), fallback: `Mapped: ${summary.mapped_count}` })} />
              <Chip label={t("payment_methods_summary_donors_affected", { count: summary.donors_affected, fallback: `Customers: ${summary.donors_affected}` })} />
            </Box>
          )}

          <LoadingContainer loading={loadingMapData}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("payment_methods_table_date", { fallback: "Date" })}</TableCell>
                  <TableCell align="right">{t("payment_methods_table_amount", { fallback: "Amount" })}</TableCell>
                  <TableCell>{t("payment_methods_table_campaign", { fallback: "Campaign" })}</TableCell>
                  <TableCell>{t("payment_methods_table_payment_type", { fallback: "Type" })}</TableCell>
                  <TableCell>{t("payment_methods_table_payment_info", { fallback: "Payment Info" })}</TableCell>
                  <TableCell>{t("payment_methods_table_donor", { fallback: "Customer" })}</TableCell>
                  <TableCell>{t("payment_methods_table_map", { fallback: "Map" })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappingRows.map((row) => {
                  const alreadyMapped = !!(row.donor && row.donor._id);
                  return (
                    <TableRow key={row.transaction_id} sx={alreadyMapped ? { bgcolor: "rgba(255,0,0,0.07)" } : {}}>
                      <TableCell>{dayjs(row.date_of_transaction).format("YYYY-MM-DD")}</TableCell>
                      <TableCell align="right">${numberWithCommas(row.amount)}</TableCell>
                      <TableCell>{row.campaign?.name || "—"}</TableCell>
                      <TableCell>{row.payment_type}</TableCell>
                      <TableCell>{row.payment_method_info}</TableCell>
                      <TableCell>{row.donor?.email || "NONE"}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={!!checked[row.transaction_id]}
                          onChange={() => toggleCheck(row.transaction_id)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </LoadingContainer>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setMapOpen(false)}>
            {t("payment_methods_cancel", { fallback: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMap}
            disabled={Object.values(checked).every((v) => !v) || mapMutation.isPending}
          >
            {mapMutation.isPending
              ? t("payment_methods_mapping_starting", { fallback: "Mapping..." })
              : t("payment_methods_mapping_start", { fallback: "Start Mapping" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
